import React, { useEffect, useMemo, useState } from 'react';
import { Form, Modal } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
// Hooks
import {
  useBirthDateFields,
  useExternalTalentModal,
  useGetSports,
} from 'hooks';
// Helpers
import {
  getExternalTalentSportName,
  getInitialValues,
  ExternalTalent,
  ExternalTalentFormValues,
} from 'helpers/externalTalents';
// Components
import BasicInfoSection from '../BasicInfoSection/BasicInfoSection';
import CareerDetailsSection from '../CareerDetailsSection/CareerDetailsSection';
import ContactsSection from '../ContactsSection/ContactsSection';
import LocationSection from '../LocationSection/LocationSection';
import SocialsSection from '../SocialsSection/SocialsSection';

type CreateEditExternalTalentModalProps = {
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  talent?: ExternalTalent | null;
};

const CreateEditExternalTalentModal = ({
  isVisible,
  onClose,
  onSuccess,
  talent,
}: CreateEditExternalTalentModalProps): JSX.Element => {
  const [form] = Form.useForm<ExternalTalentFormValues>();
  const [avatarFileList, setAvatarFileList] = useState<UploadFile[]>([]);

  const initialValues = useMemo(() => getInitialValues(talent), [talent]);
  const { data: sportsData, loading: loadingSports } = useGetSports();
  const sports = useMemo(() => sportsData?.sports || [], [sportsData]);

  const { handleSubmit, isSubmitting } = useExternalTalentModal({
    avatarFileList,
    form,
    onClose,
    onSuccess,
    talent,
  });

  const {
    birthDateOptions,
    handleBirthMonthChange,
    handleBirthYearChange,
    selectedBirthMonth,
    selectedBirthYear,
  } = useBirthDateFields({
    form,
    initialValues,
    isOpen: isVisible,
  });

  useEffect(() => {
    if (isVisible) {
      form.setFieldsValue(initialValues);
      setAvatarFileList([]);
    }
  }, [form, initialValues, isVisible]);

  useEffect(() => {
    if (!isVisible || !sports.length) {
      return;
    }

    const resolvedSportName = getExternalTalentSportName(
      initialValues.sport,
      sports
    );

    if (resolvedSportName && resolvedSportName !== initialValues.sport) {
      form.setFieldsValue({
        sport: resolvedSportName,
      });
    }
  }, [form, initialValues.sport, isVisible, sports]);

  return (
    <Modal
      cancelButtonProps={{ loading: isSubmitting }}
      closable={!isSubmitting}
      destroyOnClose
      maskClosable={!isSubmitting}
      onCancel={onClose}
      onOk={handleSubmit}
      okButtonProps={{ loading: isSubmitting }}
      okText={talent ? 'Save' : 'Create'}
      title={talent ? 'Edit External Talent' : 'Create New External Talent'}
      visible={isVisible}
      width={1000}
    >
      <Form
        form={form}
        initialValues={initialValues}
        layout="vertical"
        name="createEditExternalTalent"
      >
        <BasicInfoSection
          avatarFileList={avatarFileList}
          birthDateOptions={birthDateOptions}
          onBirthMonthChange={handleBirthMonthChange}
          onBirthYearChange={handleBirthYearChange}
          selectedBirthMonth={selectedBirthMonth}
          selectedBirthYear={selectedBirthYear}
          setAvatarFileList={setAvatarFileList}
          talent={talent}
        />
        <LocationSection disableBrowserAutocomplete form={form} />
        <CareerDetailsSection
          currentSport={initialValues.sport}
          loadingSports={loadingSports}
          sports={sports}
        />
        <SocialsSection form={form} />
        <ContactsSection form={form} />
      </Form>
    </Modal>
  );
};

export default CreateEditExternalTalentModal;
